import { Food, getFoodReferenceWeight, INFOODS, Nutrients } from '@cf-shared/data-model/food';
import {
  addIngredientIntoMealTotal,
  Ingredient,
  isIngredientMacro,
  isIngredientReal,
  newUnusableNutrientInput,
  nutrientsForWeightForDisplay,
} from '@cf-shared/data-model/ingredient';
import { RoundToDecimals } from '@cf-shared/shared-util';
import {
  describeIngredientForReport,
  NutrientDataQualityReporter,
  reportMissingNutrientMap,
  reportUnusableNutrientInput,
} from '../../util/nutrient-data-quality-reporter';

/**
 * The arithmetic behind the ingredient list's LIVE total (df-2310dc2e8f12) -- the figure the
 * meal and recipe editors show while the user edits, and which `edit-food` saves as a recipe's
 * `nutrients`.
 *
 * `IngredientList.updateNutrientsAsync` reads the foods and then hands everything to this
 * function. It lives here, apart from the component, because the component pulls in the
 * Angular DI graph and cannot be loaded by the unit test runner; `ingredient-list-totals.test.ts`
 * runs these rules with real numbers.
 *
 * It is deliberately NOT `sumSavedMealNutrients` (`services/meal-nutrient-total.ts`), whose
 * rules differ: this list skips an ingredient with no usable weight before scaling it, skips a
 * missing food in silence (the list reads with `TreatAsMissing`), adds up a total weight, and
 * produces per-row display figures. Sharing one function would mean deciding, for each of those,
 * whether the difference is intended. What IS shared is the step inside the loop that adds one
 * ingredient into the total, `addIngredientIntoMealTotal` (df-bb517784c49c).
 */

/** Where the list's reports are filed in Sentry. */
const CALL_SITE = 'ingredient-list.totals';

export interface IngredientListTotals {
  /** the list's total, each nutrient rounded to 4 significant digits */
  nutrients: Nutrients;
  /** the weight of every ingredient except typed-in ones, in grams */
  totalWeight_g: number;
  /**
   * each real ingredient's scaled figures, for display only (never persisted). A nutrient
   * nobody measured stays "not measured" here, where the total above counts it as zero.
   */
  displayNutrients: Map<Ingredient, Nutrients>;
}

/**
 * Add up the ingredient list from its ingredients and the foods already read for them.
 *
 * `foodsById` holds what the read returned for each real ingredient's food id: the food, or
 * `null` when it was deleted or never synced. An id missing from the map is treated the same
 * way. Either one contributes nothing to the nutrients, silently -- but its weight still counts
 * towards the total weight, as it always has.
 *
 * Also writes each typed-in ingredient's cached `kcal`, which the list has always done here.
 */
export function sumIngredientListNutrients(
  ingredients: readonly Ingredient[],
  foodsById: ReadonlyMap<string, Food | null>,
  logging: NutrientDataQualityReporter,
  mealId: string | undefined,
): IngredientListTotals {
  const nutrients: Nutrients = {};
  const displayNutrients = new Map<Ingredient, Nutrients>();
  // one collector for the whole list, reported once after it -- so a damaged value in the list
  // produces one Sentry event, not one per ingredient or per redraw
  const unusable = newUnusableNutrientInput();
  let totalWeight_g = 0;

  for (const [ingredientIndex, ingredient] of ingredients.entries()) {
    // an ingredient with no usable weight contributes nothing at all, not even to the weight
    if (ingredient.weight_g === undefined || isNaN(ingredient.weight_g)) {
      continue;
    }
    if (!isIngredientMacro(ingredient)) {
      totalWeight_g += ingredient.weight_g;
    }

    if (isIngredientReal(ingredient)) {
      const food = foodsById.get(ingredient.foodId);
      // if food was deleted or never synced
      if (!food) continue;
      // a food record with no nutrient map at all -- the collector has no key to record it
      // against, so it is reported on its own and contributes nothing
      if (!food.nutrients) {
        reportNoStoredNutrients(logging, mealId, ingredient);
        continue;
      }
      // what this food contributes at this weight, recorded under a label so the report can
      // say which food held a bad value (#6322)
      addIngredientIntoMealTotal(nutrients, ingredient, food, unusable, describeIngredientForReport(ingredient, ingredientIndex));

      // no `unusable` here: the line above has already reported this food's values once
      displayNutrients.set(ingredient, nutrientsForWeightForDisplay(food.nutrients, ingredient.weight_g, getFoodReferenceWeight(food)));
    } else if (isIngredientMacro(ingredient)) {
      // as above, but here it is the ingredient itself that carries no nutrient map
      if (!ingredient.nutrients) {
        reportNoStoredNutrients(logging, mealId, ingredient);
        continue;
      }
      // a typed-in ingredient stores its numbers per 100 g, which the helper knows
      const scaled = addIngredientIntoMealTotal(nutrients, ingredient, undefined, unusable, describeIngredientForReport(ingredient, ingredientIndex));

      ingredient.kcal = scaled[INFOODS.Calories];
    }
  }

  // report whatever the scaling had to skip, once for the whole list. Safe to call when nothing
  // was recorded, which is the normal case.
  reportUnusableNutrientInput(unusable, logging, CALL_SITE, { mealId });

  // round all nutrients to sensible values
  for (const key of Object.keys(nutrients)) {
    nutrients[key] = RoundToDecimals(nutrients[key], 4);
  }
  return { nutrients, totalWeight_g, displayNutrients };
}

/**
 * Report that there are no stored nutrients to add up for this ingredient -- the FOOD record's
 * map for a real ingredient, the ingredient's own for a typed-in one. Both ids go in the context
 * so whoever triages it can tell which.
 */
function reportNoStoredNutrients(logging: NutrientDataQualityReporter, mealId: string | undefined, ingredient: Ingredient) {
  reportMissingNutrientMap(logging, CALL_SITE, {
    mealId,
    foodId: isIngredientReal(ingredient) ? ingredient.foodId : undefined,
    ingredientWeight_g: ingredient.weight_g,
  });
}
